/**
 * Client-side format validation for Azure Storage account-key connection strings (ACME-1883).
 * Format only — does not call Azure. SAS / SharedAccessSignature strings are out of scope.
 */

export const AZURE_CONNECTION_STRING_REQUIRED_WARNING = 'Connection String is required';

export const AZURE_CONNECTION_STRING_FORMAT_WARNING =
    'Enter a valid Azure Storage connection string (DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net)';

const MAX_LENGTH = 256;
const ACCOUNT_NAME_RE = /^[a-z0-9]{3,24}$/;
const ACCOUNT_KEY_RE = /^[A-Za-z0-9+/]+={0,2}$/;
const ACCOUNT_KEY_LENGTH = 88;
const ALLOWED_ENDPOINT_SUFFIXES = new Set([
    'core.windows.net',
    'core.usgovcloudapi.net',
    'core.chinacloudapi.cn',
    'core.cloudapi.de',
]);

function stripAllWhitespace(value: string): string {
    return value.replace(/\s+/g, '');
}

export function isMaskedAzureConnectionString(value: string | undefined | null): boolean {
    const v = String(value ?? '');
    return v === '*****' || /^\*+$/.test(v);
}

function parsePairs(stripped: string): Map<string, string> | null {
    const segments = stripped.split(';');
    const pairs = new Map<string, string>();
    for (const segment of segments) {
        if (segment === '') return null;
        const eq = segment.indexOf('=');
        if (eq <= 0) return null;
        const key = segment.slice(0, eq);
        const val = segment.slice(eq + 1);
        if (!key || val === '') return null;
        const canon = key.toLowerCase();
        if (pairs.has(canon)) return null;
        pairs.set(canon, val);
    }
    return pairs;
}

function isValidAccountKey(key: string): boolean {
    return key.length === ACCOUNT_KEY_LENGTH && ACCOUNT_KEY_RE.test(key);
}

/** Format-only. Empty is invalid here; callers decide required vs optional. */
export function isValidAzureStorageConnectionString(value: string | undefined | null): boolean {
    const stripped = stripAllWhitespace(String(value ?? ''));
    if (!stripped || stripped.length > MAX_LENGTH) return false;
    const pairs = parsePairs(stripped);
    if (!pairs) return false;

    const protocol = pairs.get('defaultendpointsprotocol');
    if (protocol !== 'https') return false;

    const accountName = pairs.get('accountname');
    if (!accountName || !ACCOUNT_NAME_RE.test(accountName)) return false;

    const accountKey = pairs.get('accountkey');
    if (!accountKey || !isValidAccountKey(accountKey)) return false;

    if (pairs.has('endpointsuffix')) {
        const suffix = pairs.get('endpointsuffix') ?? '';
        if (!ALLOWED_ENDPOINT_SUFFIXES.has(suffix)) return false;
    }

    return true;
}

export function azureConnectionStringWarning(
    value: string | undefined | null,
    options?: { required?: boolean }
): string | null {
    const stripped = stripAllWhitespace(String(value ?? ''));
    const required = options?.required !== false;
    if (!stripped) {
        return required ? AZURE_CONNECTION_STRING_REQUIRED_WARNING : null;
    }
    if (isMaskedAzureConnectionString(stripped)) return null;
    if (isValidAzureStorageConnectionString(stripped)) return null;
    return AZURE_CONNECTION_STRING_FORMAT_WARNING;
}
